import type {
	ApplicationListParams,
	ApplicationListResponse,
	SubjectsFilterResponse,
} from "@/types/application";
import type {
	ApplicationResult,
	TRIPerformanceResponse,
} from "@/types/application-result";
import type { QuestionDetail } from "@/types/question-detail";
import type { DashboardApplicationList } from "@/types/student-dashboard";
import type {
	SubjectPerformanceChart,
	SubjectPerformanceDetail,
} from "@/types/subject-performance";
import type {
	ApplicationExecution,
	ApplicationIntro,
	FinishApplicationResponse,
	FinishLeaveTabResponse,
	LeaveTabResponse,
	SubmitAnswerResponse,
} from "@/types/test-execution";
import { api } from "./api";

function pickNonEmptyString(
	...values: (string | null | undefined)[]
): string | undefined {
	for (const v of values) {
		if (typeof v === "string" && v.trim().length > 0) return v.trim();
	}
	return undefined;
}

/** Alinha respostas da API (camelCase vs snake_case) com o modelo do app. */
function normalizeQuestionDetailPayload(raw: QuestionDetail): QuestionDetail {
	if (!raw.answer) return raw;

	const loose = raw.answer as QuestionDetail["answer"] & {
		answer_file?: string | null;
		answer_text?: string | null;
		checked_answers?: (string | number)[];
		file_url?: string | null;
		fileUrl?: string | null;
	};

	const checkedAnswers = Array.isArray(loose.checkedAnswers)
		? loose.checkedAnswers
		: Array.isArray(loose.checked_answers)
			? loose.checked_answers
			: [];

	const answerFile = pickNonEmptyString(
		loose.answerFile,
		loose.answer_file,
		loose.fileUrl,
		loose.file_url,
	);

	const answerText = pickNonEmptyString(loose.answerText, loose.answer_text);

	return {
		...raw,
		answer: {
			checkedAnswers,
			answerFile,
			answerText,
		},
	};
}

export const applicationService = () => ({
	getAvailablesToday: async (): Promise<DashboardApplicationList> => {
		const response = await api.get<DashboardApplicationList>(
			"applications/availables_today/",
		);
		return response.data;
	},

	getRecentlyReleasedResults: async (): Promise<DashboardApplicationList> => {
		const response = await api.get<DashboardApplicationList>("applications/", {
			params: { recently_released_results: true },
		});
		return response.data;
	},

	getApplications: async (
		params: ApplicationListParams = {},
	): Promise<ApplicationListResponse> => {
		const {
			status,
			name,
			year,
			date,
			subject,
			category,
			category__in,
			page,
			page_size = 10,
		} = params;

		let url = "applications/";
		if (status === "disponiveis") {
			url = "applications/availables_today/";
		}

		const response = await api.get<ApplicationListResponse>(url, {
			params: {
				page_size,
				only_scheduled: status === "agendadas" ? true : undefined,
				without_scheduled: status === "liberadas" ? true : undefined,
				application__exam__name__icontains: name,
				application__date__year: year,
				application__date: date,
				application__exam__teacher_subjects__subject__in: subject,
				application__category: category,
				application__category__in:
					category__in && category__in.length > 0
						? category__in.join(",")
						: undefined,
				category, // Some endpoints might use the direct name
				page,
			},
		});
		return response.data;
	},

	getSubjectsFilters: async (): Promise<SubjectsFilterResponse> => {
		const response = await api.get<SubjectsFilterResponse>(
			"applications/get_filters/",
		);
		return response.data;
	},

	redoApplication: async (id: string): Promise<unknown> => {
		const response = await api.get(`applications/${id}/redo_application/`);
		return response.data;
	},

	getApplicationResult: async (id: string): Promise<ApplicationResult> => {
		const response = await api.get<ApplicationResult>(
			`applications/${id}/result/`,
		);
		return response.data;
	},

	getSubjectPerformance: async (
		applicationId: string,
		subjectId: string,
	): Promise<SubjectPerformanceDetail> => {
		const response = await api.get<SubjectPerformanceDetail>(
			`applications/${applicationId}/subjects/${subjectId}/`,
		);
		return response.data;
	},

	getSubjectPerformanceChart: async (
		applicationId: string,
		subjectId: string,
	): Promise<SubjectPerformanceChart> => {
		const response = await api.get<SubjectPerformanceChart>(
			`applications/${applicationId}/subjects/${subjectId}/chart/`,
		);
		return response.data;
	},

	getQuestionDetail: async (
		applicationId: string,
		questionId: string | number,
	): Promise<QuestionDetail> => {
		const response = await api.get<QuestionDetail>(
			`applications/${applicationId}/question_detail_with_answer/`,
			{
				params: { question_id: questionId },
			},
		);
		return normalizeQuestionDetailPayload(response.data);
	},

	getTRIPerformance: async (
		studentId: string | number,
		examId: string,
	): Promise<TRIPerformanceResponse> => {
		const response = await api.post<TRIPerformanceResponse>(
			`applications/${examId}/tri/`,
			{
				exams: [examId],
				year: 2025,
				students: [studentId],
				ignoreCache: false,
			},
		);
		return response.data;
	},

	getApplicationIntro: async (id: string): Promise<ApplicationIntro> => {
		const response = await api.get<ApplicationIntro>(
			`applications/${id}/intro/`,
		);
		return response.data;
	},

	getApplicationExecution: async (
		id: string,
	): Promise<ApplicationExecution> => {
		const response = await api.get<ApplicationExecution>(
			`applications/${id}/take_test/`,
		);
		return response.data;
	},

	startApplication: async (id: string): Promise<unknown> => {
		const response = await api.patch(`applications/${id}/start/`, {
			startTime: new Date().toISOString(),
		});
		return response.data;
	},

	submitAnswer: async (
		id: string,
		data: {
			examQuestion: string | number;
			subject_id: string | number;
			category: string;
			options?: { index: number; option: string | number }[];
			text?: string;
		},
	): Promise<SubmitAnswerResponse> => {
		const response = await api.post<SubmitAnswerResponse>(
			`applications/${id}/create_answer/`,
			data,
		);
		return response.data;
	},

	finishApplication: async (id: string): Promise<FinishApplicationResponse> => {
		const response = await api.patch(`applications/${id}/finish/`, {
			endTime: new Date().toISOString(),
		});
		// In the legacy, this returns a response object with ok/status
		// We'll normalize it here if needed, but for now just returning data
		return {
			success: true,
			message: "Prova finalizada com sucesso",
			...response.data,
		};
	},

	checkApplicationToken: async (
		id: string,
		token: string,
		fingerprintId: string,
	): Promise<{ status: string; message?: string }> => {
		const trimmedToken = token.trim();
		const trimmedFp = fingerprintId.trim();
		if (!trimmedFp) {
			throw new Error(
				"Identificador do dispositivo ausente. Recarregue a página e tente novamente.",
			);
		}

		const response = await api.post<{ status: string; message?: string }>(
			`applications/${id}/token/`,
			{
				token: trimmedToken,
				fingerprintId: trimmedFp,
				fingerprint_id: trimmedFp,
			},
		);
		const body = response.data;
		const status =
			typeof body.status === "string"
				? body.status.trim().toLowerCase().replaceAll("_", "-")
				: "";

		if (status !== "success") {
			const fallbackByStatus: Record<string, string> = {
				"invalid-fingerprint":
					"Dispositivo não reconhecido. Use o mesmo aparelho em que você começou esta prova.",
				"invalid-token": "Código de acesso incorreto.",
				"application-no-token":
					"Esta aplicação não está configurada para exigir código de acesso.",
			};
			throw new Error(
				body.message ??
					fallbackByStatus[status] ??
					"Não foi possível validar o código de acesso.",
			);
		}
		return body;
	},

	leaveTab: async (id: string): Promise<LeaveTabResponse> => {
		const response = await api.post<LeaveTabResponse>(
			`applications/${id}/leave-tab/`,
		);
		return response.data;
	},

	finishLeaveTab: async (
		id: string,
		eventPk: number | string,
	): Promise<FinishLeaveTabResponse> => {
		const response = await api.patch<FinishLeaveTabResponse>(
			`applications/${id}/leave-tab/${eventPk}/finish/`,
		);
		return response.data;
	},

	submitFileAnswer: async (
		applicationId: string,
		examQuestionId: string | number,
		file: File,
	): Promise<unknown> => {
		const formData = new FormData();
		formData.append("exam_question", examQuestionId.toString());
		formData.append("category", "file");
		formData.append("file", file);

		const response = await api.post(
			`applications/${applicationId}/create_answer/`,
			formData,
			{
				headers: { "Content-Type": "multipart/form-data" },
			},
		);
		return response.data;
	},

	reportQuestionError: async (
		applicationId: string,
		examQuestionId: string | number,
		content: string,
	): Promise<{ success: boolean; message?: string }> => {
		const response = await api.post(
			`applications/${applicationId}/report-question-error/`,
			{ examQuestion: examQuestionId, content },
		);
		return response.data;
	},
});
